import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchUsers, createUser, updateUser, deleteUser,
  activateUser, deactivateUser
} from '../../store/slices/usersSlice';
import { fetchProjects } from '../../store/slices/projectsSlice';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import api from '../../services/api';
import { validationMessages, isValidEmail } from '../../utils/validationMessages';
import './Users.css';

const Users = () => {
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector((state) => state.auth);
  const { users, pagination, loading } = useSelector((state) => state.users);
  const { projects } = useSelector((state) => state.projects);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [loadingUserProjects, setLoadingUserProjects] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'user',
    auth_provider: 'azure',
  });
  const [formErrors, setFormErrors] = useState({});
  const [inviting, setInviting] = useState(false);
  
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: null,
    isLoading: false
  });

  const [updatingRoleFor, setUpdatingRoleFor] = useState(null);
  const [togglingReportingFor, setTogglingReportingFor] = useState(null);

  const canInviteUser = ['super_admin', 'admin'].includes(currentUser?.role);
  const canDeleteUser = ['super_admin', 'admin'].includes(currentUser?.role);
  const canManageProjects = ['super_admin', 'admin'].includes(currentUser?.role);
  const canChangeRoles = ['super_admin', 'admin'].includes(currentUser?.role);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, roleFilter]);

  useEffect(() => {
    dispatch(fetchUsers({ page: currentPage, pageSize: 20, search, role: roleFilter }));
  }, [dispatch, currentPage, search, roleFilter]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (canManageProjects) {
      dispatch(fetchProjects({ page: 1, pageSize: 500 }));
    }
  }, [dispatch, canManageProjects]);

  const handleManageProjects = async (user) => {
    setSelectedUser(user);
    setShowProjectsModal(true);
    setProjectSearch('');
    setLoadingUserProjects(true);

    try {
      const response = await api.get(`/users/${user.id}/projects`);
      setUserProjects(response.data.projects || []);
    } catch (error) {
      toast.error('Failed to load user projects');
      setUserProjects([]);
    } finally {
      setLoadingUserProjects(false);
    }
  };

  const handleAssignProject = async (projectId, role = 'member') => {
    if (!selectedUser) return;

    try {
      await api.post(`/users/${selectedUser.id}/projects`, {
        assignments: [{ project_id: projectId, role }]
      });
      toast.success('Project assigned');

      // Refresh user projects
      const response = await api.get(`/users/${selectedUser.id}/projects`);
      setUserProjects(response.data.projects || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign project');
    }
  };

  const handleUpdateProjectRole = async (projectId, newRole) => {
    if (!selectedUser) return;

    try {
      await api.put(`/users/${selectedUser.id}/projects/${projectId}`, { role: newRole });
      toast.success('Role updated');

      // Refresh user projects
      const response = await api.get(`/users/${selectedUser.id}/projects`);
      setUserProjects(response.data.projects || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveFromProject = (projectId, projectName) => {
    if (!selectedUser) return;
    setConfirmModal({
      isOpen: true,
      title: 'Remove from Project',
      message: `Are you sure you want to remove "${selectedUser.first_name} ${selectedUser.last_name}" from "${projectName}"?`,
      confirmText: 'Remove',
      variant: 'warning',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/users/${selectedUser.id}/projects/${projectId}`);
          toast.success('User removed from project');
          const response = await api.get(`/users/${selectedUser.id}/projects`);
          setUserProjects(response.data.projects || []);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to remove from project');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const projectSearchLower = projectSearch.toLowerCase();

  const filteredUserProjects = userProjects.filter(up =>
    !projectSearch ||
    up.project_name.toLowerCase().includes(projectSearchLower) ||
    up.project_key.toLowerCase().includes(projectSearchLower)
  );

  // Get projects not yet assigned to user
  const availableProjects = projects.filter(
    p => !userProjects.some(up => up.project_id === p.id)
  );

  const filteredAvailableProjects = availableProjects.filter(p =>
    !projectSearch ||
    p.name.toLowerCase().includes(projectSearchLower) ||
    p.key.toLowerCase().includes(projectSearchLower)
  );

  const validateInviteForm = () => {
    const errors = {};
    if (!inviteFormData.email) {
      errors.email = validationMessages.user.email.required;
    } else if (!/\S+@\S+\.\S+/.test(inviteFormData.email)) {
      errors.email = validationMessages.user.email.format;
    }
    if (!inviteFormData.first_name) errors.first_name = validationMessages.user.firstName.required;
    if (!inviteFormData.last_name) errors.last_name = validationMessages.user.lastName.required;
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!validateInviteForm()) return;

    setInviting(true);
    try {
      await api.post('/invitation/invite', inviteFormData);
      toast.success('Invitation sent successfully! The user will receive an email to set up their account.');
      setShowInviteModal(false);
      setInviteFormData({ email: '', first_name: '', last_name: '', role: 'user', auth_provider: 'azure' });
      // Refresh users list
      dispatch(fetchUsers({ page: currentPage, pageSize: 20, search, role: roleFilter }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvitation = async (userId) => {
    try {
      await api.post(`/invitation/resend/${userId}`);
      toast.success('Invitation resent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = (userId, userName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cancel Invitation',
      message: `Are you sure you want to cancel the invitation for "${userName}"? This will permanently delete the pending user.`,
      confirmText: 'Cancel Invitation',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/invitation/cancel/${userId}`);
          toast.success('Invitation cancelled');
          dispatch(fetchUsers({ page: currentPage, pageSize: 20, search, role: roleFilter }));
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to cancel invitation');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleToggleActive = (userId, isActive, userName) => {
    const action = isActive ? 'deactivate' : 'activate';
    setConfirmModal({
      isOpen: true,
      title: isActive ? 'Deactivate User' : 'Activate User',
      message: isActive 
        ? `Are you sure you want to deactivate "${userName}"? They will no longer be able to log in.`
        : `Are you sure you want to activate "${userName}"? They will be able to log in again.`,
      confirmText: isActive ? 'Deactivate' : 'Activate',
      variant: isActive ? 'warning' : 'info',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          if (isActive) {
            await dispatch(deactivateUser(userId)).unwrap();
            toast.success('User deactivated');
          } else {
            await dispatch(activateUser(userId)).unwrap();
            toast.success('User activated');
          }
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error || 'Failed to update user status');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleDeleteUser = (userId, userName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete "${userName}"? This action cannot be undone.`,
      confirmText: 'Delete User',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteUser(userId)).unwrap();
          toast.success('User deleted');
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error || 'Failed to delete user');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleRoleChange = (userId, newRole, userName, currentRole) => {
    if (newRole === currentRole) return;

    setConfirmModal({
      isOpen: true,
      title: 'Change User Role',
      message: `Are you sure you want to change "${userName}" role from "${currentRole.replace('_', ' ')}" to "${newRole.replace('_', ' ')}"?${newRole === 'admin' ? ' Admin users are automatically assigned to all projects.' : ''}`,
      confirmText: 'Change Role',
      variant: 'warning',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        setUpdatingRoleFor(userId);
        try {
          await dispatch(updateUser({ userId, userData: { role: newRole } })).unwrap();
          toast.success(`Role updated to ${newRole.replace('_', ' ')}`);
          dispatch(fetchUsers({ page: currentPage, pageSize: 20, search, role: roleFilter }));
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error || 'Failed to update role');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        } finally {
          setUpdatingRoleFor(null);
        }
      }
    });
  };

  const handleToggleReporting = async (user) => {
    if (togglingReportingFor === user.id) return;
    // Admin cannot modify super_admin
    if (currentUser?.role === 'admin' && user.role === 'super_admin') return;
    setTogglingReportingFor(user.id);
    try {
      const res = await api.patch(`/users/${user.id}/reporting-access`, {
        reporting_access: !user.reporting_access,
      });
      dispatch(updateUser.fulfilled(res.data, null, null));
      dispatch(fetchUsers({ page: currentPage, pageSize: 20, search, role: roleFilter }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reporting access');
    } finally {
      setTogglingReportingFor(null);
    }
  };

  const getRoleBadge = (role) => {
    const classes = {
      super_admin: 'badge-danger',
      admin: 'badge-warning',
      manager: 'badge-primary',
      user: 'badge-success',
      viewer: 'badge-secondary',
    };
    return `badge ${classes[role] || 'badge-primary'}`;
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage system users and their roles</p>
        </div>
        {canInviteUser && (
          <button
            className="btn btn-primary"
            onClick={() => setShowInviteModal(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Invite User
          </button>
        )}
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="filters-bar">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M9.965 11.026a5 5 0 111.06-1.06l2.755 2.754a.75.75 0 11-1.06 1.06l-2.755-2.754zM10.5 7a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z" clipRule="evenodd" />
            </svg>
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="role-filter-wrapper">
            <select
              className="form-select form-select-sm"
              style={{ marginBottom: '0px' }}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="user">User</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="loading-state">
            <span className="spinner"></span>
            <p>Loading users...</p>
          </div>
        ) : users.length > 0 ? (
          <div className="users-table-container">
            <table className="table users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Reporting
                      <span style={{
                        fontSize: 10, fontWeight: 500, color: 'var(--color-text-tertiary)',
                        background: 'var(--color-bg-hover)', borderRadius: 4, padding: '1px 5px',
                      }}>Access</span>
                    </div>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <div className="avatar">
                          {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                        </div>
                        <span>{user.first_name} {user.last_name}</span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      {canChangeRoles && user.role !== 'super_admin' && user.id !== currentUser?.id && !user.invitation_pending ? (
                        <select
                          className="form-select form-select-sm role-change-select"
                          style={{ marginBottom: '0px', width: 'auto', minWidth: '110px' }}
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value, `${user.first_name} ${user.last_name}`, user.role)}
                          disabled={updatingRoleFor === user.id}
                        >
                          <option value="viewer">Viewer</option>
                          <option value="user">User</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className={getRoleBadge(user.role)}>
                          {user.role.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        {user.invitation_pending ? (
                          <span className="badge badge-warning">Pending Invitation</span>
                        ) : (
                          <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                        {user.auth_provider === 'local' && (
                          <span className="badge badge-external">External</span>
                        )}
                      </div>
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      {canChangeRoles ? (
                        <label className="reporting-toggle-label" title={
                          currentUser?.role === 'admin' && user.role === 'super_admin'
                            ? 'Cannot modify super admin'
                            : user.reporting_access ? 'Click to revoke reporting access' : 'Click to grant reporting access'
                        }>
                          <input
                            type="checkbox"
                            className="reporting-toggle-input"
                            checked={!!user.reporting_access}
                            disabled={
                              togglingReportingFor === user.id ||
                              (currentUser?.role === 'admin' && user.role === 'super_admin')
                            }
                            onChange={() => handleToggleReporting(user)}
                          />
                          <span className={`reporting-toggle-track${user.reporting_access ? ' on' : ''}`}>
                            <span className="reporting-toggle-thumb" />
                          </span>
                          <span className="reporting-toggle-status">
                            {togglingReportingFor === user.id
                              ? 'Saving…'
                              : user.reporting_access ? 'Enabled' : 'Disabled'}
                          </span>
                        </label>
                      ) : (
                        <span className={`badge ${user.reporting_access ? 'badge-success' : 'badge-secondary'}`}>
                          {user.reporting_access ? 'Enabled' : 'Disabled'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        {user.invitation_pending ? (
                          /* Actions for pending invitation users */
                          canInviteUser && (
                            <>
                              <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => handleResendInvitation(user.id)}
                                title="Resend Invitation"
                              >
                                📧 Resend
                              </button>
                              <button
                                className="btn btn-sm btn-ghost btn-danger-hover"
                                onClick={() => handleCancelInvitation(user.id, `${user.first_name} ${user.last_name}`)}
                                title="Cancel Invitation"
                              >
                                Cancel
                              </button>
                            </>
                          )
                        ) : (
                          /* Actions for active/inactive users */
                          <>
                            {canManageProjects && (
                              <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => handleManageProjects(user)}
                                title="Manage Projects"
                              >
                                📁 Projects
                              </button>
                            )}
                            {user.role !== 'super_admin' && user.id !== currentUser?.id && canInviteUser && (
                              <>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  onClick={() => handleToggleActive(user.id, user.is_active, `${user.first_name} ${user.last_name}`)}
                                >
                                  {user.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                                {canDeleteUser && (
                                  <button
                                    className="btn btn-sm btn-ghost btn-danger-hover"
                                    onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pagination.totalPages > 1 && (
              <div className="pagination-bar">
                <span className="pagination-info">
                  {((currentPage - 1) * 20) + 1}–{Math.min(currentPage * 20, pagination.total)} of {pagination.total} users
                </span>
                <div className="pagination-controls">
                  <button
                    className="btn btn-sm btn-ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    ‹ Prev
                  </button>
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - currentPage) <= 2)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                      ) : (
                        <button
                          key={p}
                          className={`btn btn-sm ${p === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => handlePageChange(p)}
                        >
                          {p}
                        </button>
                      )
                    )}
                  <button
                    className="btn btn-sm btn-ghost"
                    disabled={currentPage === pagination.totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state">
            <p>No users found</p>
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite New User"
      >
        <form onSubmit={handleInviteUser}>
          <div className="invite-info-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p>
              {inviteFormData.auth_provider === 'local'
                ? 'The user will receive an invitation email and will set up a password to activate their account.'
                : 'The user will receive an invitation email and will activate their account by signing in with Microsoft.'}
            </p>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name <span className="required">*</span></label>
              <input
                type="text"
                className={`form-input ${formErrors.first_name ? 'error' : ''}`}
                value={inviteFormData.first_name}
                onChange={(e) => setInviteFormData({ ...inviteFormData, first_name: e.target.value })}
                placeholder="Enter first name"
              />
              {formErrors.first_name && <span className="form-error">{formErrors.first_name}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span className="required">*</span></label>
              <input
                type="text"
                className={`form-input ${formErrors.last_name ? 'error' : ''}`}
                value={inviteFormData.last_name}
                onChange={(e) => setInviteFormData({ ...inviteFormData, last_name: e.target.value })}
                placeholder="Enter last name"
              />
              {formErrors.last_name && <span className="form-error">{formErrors.last_name}</span>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email Address <span className="required">*</span></label>
            <input
              type="email"
              className={`form-input ${formErrors.email ? 'error' : ''}`}
              value={inviteFormData.email}
              onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
              placeholder="Enter email address"
            />
            {formErrors.email && <span className="form-error">{formErrors.email}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <select
              className="form-select"
              value={inviteFormData.role}
              onChange={(e) => setInviteFormData({ ...inviteFormData, role: e.target.value })}
            >
              <option value="viewer">Viewer</option>
              <option value="user">User</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
            <p className="form-help">The user's role determines their permissions in the system.</p>
          </div>

          <div className="form-group">
            <label className="form-label">Authentication Method</label>
            <div className="auth-provider-options">
              <label className="auth-provider-option">
                <input
                  type="radio"
                  name="auth_provider"
                  value="azure"
                  checked={inviteFormData.auth_provider === 'azure'}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, auth_provider: e.target.value })}
                />
                <span className="auth-provider-label">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" width="16" height="16" aria-hidden="true" style={{ flexShrink: 0 }}>
                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                  Microsoft (Azure SSO)
                </span>
              </label>
              <label className="auth-provider-option">
                <input
                  type="radio"
                  name="auth_provider"
                  value="local"
                  checked={inviteFormData.auth_provider === 'local'}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, auth_provider: e.target.value })}
                />
                <span className="auth-provider-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M3 9L12 14L21 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  External (Email &amp; Password)
                </span>
              </label>
            </div>
            <p className="form-help">
              {inviteFormData.auth_provider === 'local'
                ? 'User will log in with their email and a password they set up via the invitation link.'
                : 'User will log in using their Microsoft organisational account.'}
            </p>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary" style={{ marginTop: '0px' }}
              onClick={() => setShowInviteModal(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={inviting}
            >
              {inviting ? (
                <>
                  <span className="spinner"></span>
                  Sending Invitation...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px' }}>
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Manage Projects Modal */}
      <Modal
        isOpen={showProjectsModal}
        onClose={() => {
          setShowProjectsModal(false);
          setSelectedUser(null);
          setUserProjects([]);
          setProjectSearch('');
        }}
        title={selectedUser ? `Manage Projects - ${selectedUser.first_name} ${selectedUser.last_name}` : 'Manage Projects'}
        size="large"
      >
        <div className="user-projects-modal">
          {loadingUserProjects ? (
            <div className="loading-state">
              <span className="spinner"></span>
              <p>Loading projects...</p>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="modal-project-search">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M9.965 11.026a5 5 0 111.06-1.06l2.755 2.754a.75.75 0 11-1.06 1.06l-2.755-2.754zM10.5 7a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z" clipRule="evenodd"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search projects by name or key…"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  autoFocus
                />
                {projectSearch && (
                  <button
                    className="modal-search-clear"
                    onClick={() => setProjectSearch('')}
                    title="Clear search"
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 11-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z"/>
                    </svg>
                  </button>
                )}
              </div>

              {/* Assigned Projects */}
              <div className="projects-section">
                <h4>
                  Assigned ({filteredUserProjects.length}
                  {projectSearch && filteredUserProjects.length !== userProjects.length ? ` of ${userProjects.length}` : ''})
                </h4>
                {filteredUserProjects.length > 0 ? (
                  <div className="assigned-projects-list">
                    {filteredUserProjects.map((up) => (
                      <div key={up.project_id} className="project-assignment-item">
                        <div className="project-info">
                          <span className="project-key">{up.project_key}</span>
                          <span className="project-name">{up.project_name}</span>
                        </div>
                        <div className="project-role">
                          {canManageProjects ? (
                            <select
                              className="form-select form-select-sm"
                              value={up.role}
                              onChange={(e) => handleUpdateProjectRole(up.project_id, e.target.value)}
                            >
                              <option value="member">Member</option>
                              <option value="manager">Manager</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className={`badge ${up.role === 'admin' ? 'badge-warning' : up.role === 'manager' ? 'badge-primary' : 'badge-secondary'}`}>
                              {up.role}
                            </span>
                          )}
                        </div>
                        {canManageProjects && (
                          <button
                            className="btn btn-sm btn-ghost btn-danger-hover"
                            onClick={() => handleRemoveFromProject(up.project_id, up.project_name)}
                            title="Remove from project"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-projects">
                    {projectSearch ? `No assigned projects match "${projectSearch}"` : 'No projects assigned'}
                  </p>
                )}
              </div>

              {/* Available Projects — only admins can assign */}
              {canManageProjects && (filteredAvailableProjects.length > 0 || (projectSearch && availableProjects.length > 0)) && (
                <div className="projects-section">
                  <h4>
                    Available ({filteredAvailableProjects.length}
                    {projectSearch && filteredAvailableProjects.length !== availableProjects.length ? ` of ${availableProjects.length}` : ''})
                  </h4>
                  {filteredAvailableProjects.length > 0 ? (
                    <div className="available-projects-list">
                      {filteredAvailableProjects.map((project) => (
                        <div key={project.id} className="project-available-item">
                          <div className="project-info">
                            <span className="project-key">{project.key}</span>
                            <span className="project-name">{project.name}</span>
                          </div>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleAssignProject(project.id)}
                          >
                            + Assign
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-projects">No available projects match &ldquo;{projectSearch}&rdquo;</p>
                  )}
                </div>
              )}
            </>
          )}

          <div className="modal-footer">
            <button
              className="btn btn-secondary" style={{ marginTop: '0px' }}
              onClick={() => {
                setShowProjectsModal(false);
                setSelectedUser(null);
                setUserProjects([]);
                setProjectSearch('');
              }}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default Users;
